import { useGetMillwrightsByOutageIdQuery } from '@/apis/millwrights/millwrightsApi';
import { MillwrightsList } from '@/pages/Millwrights/components';
import { ChangeEvent, FC, useEffect, useMemo } from 'react';
import { usePagination, useSearch } from '@/hooks';
import {
  INITIAL_PAGE,
  LIST_PAGE_SIZE,
  millwrightsSortOptions,
} from '@/constants';
import { Select, SelectOption } from '@data-products-and-ai/react-components';
import { useMillwrightsSort } from '@/pages/Millwrights/hooks';
import {
  readSessionStorageJson,
  toPascalCase,
  writeSessionStorageJson,
} from '@/utils';

interface OutageMilwrightsProps {
  outageId: number;
}

interface PersistedOutageMillwrightsFilters {
  sortValue?: string;
  search?: string;
  page?: number;
}

const getOutageMillwrightsStorageKey = (outageId: number) =>
  `outage-millwrights-filters:${outageId}`;

const parsePersistedFilters = (value: unknown) => {
  if (!value || typeof value !== 'object') return null;

  const parsed = value as PersistedOutageMillwrightsFilters;
  const page =
    typeof parsed.page === 'number' && Number.isFinite(parsed.page)
      ? Math.max(1, Math.floor(parsed.page))
      : undefined;

  return {
    sortValue:
      typeof parsed.sortValue === 'string' ? parsed.sortValue : undefined,
    search: typeof parsed.search === 'string' ? parsed.search : undefined,
    page,
  };
};

export const OutageMillwrights: FC<OutageMilwrightsProps> = ({ outageId }) => {
  const storageKey = useMemo(
    () => getOutageMillwrightsStorageKey(outageId),
    [outageId],
  );
  const persistedFilters = useMemo(
    () => readSessionStorageJson(storageKey, parsePersistedFilters),
    [storageKey],
  );
  const initialSortOption =
    millwrightsSortOptions.find(
      (option) => option.value === persistedFilters?.sortValue,
    ) ?? millwrightsSortOptions[0];

  const { debouncedQuery, query, handleSearch } = useSearch({
    initialQuery: persistedFilters?.search ?? '',
  });
  const { currentPage, pageSize, setPage } = usePagination({
    initialPage: persistedFilters?.page ?? INITIAL_PAGE,
    initialPageSize: LIST_PAGE_SIZE,
  });
  const { sortOption, updateSortOption } =
    useMillwrightsSort(initialSortOption);

  const sortBy = (toPascalCase(sortOption?.sortBy) ?? 'Name') as
    | 'Name'
    | 'Risk';

  const {
    data: millwrightsData,
    isLoading,
    isFetching,
  } = useGetMillwrightsByOutageIdQuery(
    {
      outageId,
      q: debouncedQuery,
      limit: pageSize,
      offset: (currentPage - 1) * pageSize,
      sortDir: sortOption.sortDir,
      sortBy,
    },
    {
      skip: !outageId,
    },
  );
  const millwrights = millwrightsData?.results || [];
  const totalItems = millwrightsData?.total ?? 0;
  const totalPages = Math.ceil(totalItems / pageSize);

  const handlePageChange = (page: number) => {
    setPage(page);
  };

  const handleSortChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const sortOption = millwrightsSortOptions.find(
      (option) => option.value === event.target.value,
    );

    if (sortOption) {
      updateSortOption(sortOption);
      setPage(INITIAL_PAGE);
    }
  };

  useEffect(() => {
    if (!outageId || typeof window === 'undefined') return;

    const stateToPersist: PersistedOutageMillwrightsFilters = {
      sortValue: sortOption.value,
      search: query,
      page: currentPage,
    };

    writeSessionStorageJson(storageKey, stateToPersist);
  }, [storageKey, outageId, sortOption.value, query, currentPage]);

  return (
    <MillwrightsList
      hasSearch
      hasPagination
      list={millwrights}
      totalItems={totalItems}
      totalPages={totalPages}
      currentPage={currentPage}
      header="Active millwrights"
      loading={isLoading || isFetching}
      searchQuery={query}
      sortComponent={
        <Select defaultValue={sortOption.value} onChange={handleSortChange}>
          {millwrightsSortOptions.map((option) => (
            <SelectOption
              key={option.value}
              value={option.value}
              label={option.label}
            />
          ))}
        </Select>
      }
      onSearchDone={handleSearch}
      onPageChange={handlePageChange}
    />
  );
};
